import { PermissionCode } from 'types';

/**
 * Which permission codes count as which. Implication flows broad to narrow only, so nothing
 * here grants `market_pricing`. Mirrors `api/src/utils/auth/permissionImplications.ts`; the two
 * tables must stay identical.
 */
export const IMPLIED_PERMISSIONS = {
  [PermissionCode.MARKET_PRICING]: [
    PermissionCode.VIEW_MARKET_PRICING,
    PermissionCode.COMMENT_MARKET_PRICING,
  ],
  [PermissionCode.COMMENT_MARKET_PRICING]: [PermissionCode.VIEW_MARKET_PRICING],
  [PermissionCode.VIEW_COMPOSITE_EMPLOYEES]: [PermissionCode.VIEW_MARKET_PRICING],
} as const satisfies Partial<Record<PermissionCode, readonly PermissionCode[]>>;

const impliedBy = (code: string): readonly PermissionCode[] =>
  IMPLIED_PERMISSIONS[code as keyof typeof IMPLIED_PERMISSIONS] ?? [];

/** Granted codes plus everything they imply, transitively. Unknown codes pass through. */
export function expandHeldPermissions<T extends string>(
  granted: Iterable<T>,
): Set<T | PermissionCode> {
  const effective = new Set<T | PermissionCode>(granted);
  const pending: Array<T | PermissionCode> = [...effective];

  while (pending.length) {
    for (const implied of impliedBy(pending.pop()!)) {
      if (!effective.has(implied)) {
        effective.add(implied);
        pending.push(implied);
      }
    }
  }

  return effective;
}
